import type { FillPatternEventArgs, Item, Rectangle } from "@artis/data-grid";
import { useCallback, useRef } from "react";

import { linearSeries } from "../formula/series";
import { shiftRefs } from "../formula/shift";

type FillDeps = {
  getSource: (cell: Item) => string;
  setValue: (cell: Item, raw: string) => void;
  transact: (fn: () => void) => void;
};

function inRect(cell: Item, rect: Rectangle): boolean {
  return cell[0] >= rect.x && cell[0] < rect.x + rect.width && cell[1] >= rect.y && cell[1] < rect.y + rect.height;
}

export function useFill(deps: FillDeps) {
  const depsRef = useRef(deps);
  depsRef.current = deps;

  return useCallback((e: FillPatternEventArgs) => {
    e.preventDefault();
    const { getSource, setValue, transact } = depsRef.current;
    const src = e.patternSource;
    const dest = e.fillDestination;

    const copyShifted = (cell: Item) => {
      const from: Item = [
        src.x + ((((cell[0] - src.x) % src.width) + src.width) % src.width),
        src.y + ((((cell[1] - src.y) % src.height) + src.height) % src.height),
      ];
      setValue(cell, shiftRefs(getSource(from), cell[0] - from[0], cell[1] - from[1]));
    };

    const vertical = dest.x === src.x && dest.width === src.width;
    transact(() => {
      for (let r = 0; r < dest.height; r++) {
        for (let c = 0; c < dest.width; c++) {
          const cell: Item = [dest.x + c, dest.y + r];
          if (inRect(cell, src)) continue;
          const seed = vertical
            ? Array.from({ length: src.height }, (_, i) => getSource([cell[0], src.y + i]))
            : Array.from({ length: src.width }, (_, i) => getSource([src.x + i, cell[1]]));
          const series = linearSeries(seed);
          if (series) {
            setValue(cell, series(vertical ? cell[1] - src.y : cell[0] - src.x));
          } else {
            copyShifted(cell);
          }
        }
      }
    });
  }, []);
}
